import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';

const stats = [
  { value: 14, suffix: '', key: 'stats.years' as const },
  { value: 4, suffix: '', key: 'stats.degrees' as const },
  { value: 29, suffix: '', key: 'stats.recommendations' as const },
  { value: 80, suffix: "מ'", key: 'stats.budgets' as const },
];

const Counter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-3xl md:text-4xl font-bold text-gradient-gold">
      +{count}{suffix}
    </div>
  );
};

const StatsSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-10 relative">
      <div className="absolute inset-0 bg-navy-medium/30" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map(({ value, suffix, key }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              className="text-center p-4"
            >
              <Counter target={value} suffix={suffix} />
              <p className="mt-1.5 text-xs md:text-sm text-muted-foreground">{t(key)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
